import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Upload, FileText, Clock, Users, TrendingUp, Activity, CheckCircle } from 'lucide-react';

const Dashboard = () => {
  const { user, userProfile, company, isCompanyAdmin } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalFiles: 0,
    processingFiles: 0,
    completedMatches: 0,
    activeUsers: 0
  });
  const [recentActivity, setRecentActivity] = useState([]);

  useEffect(() => {
    // Simulate loading data
    const loadDashboardData = async () => {
      try {
        setLoading(true);
        
        // Simulate API call delay
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        // Mock data - in real app this would come from Supabase
        setStats({
          totalFiles: 24,
          processingFiles: 3,
          completedMatches: 18420,
          activeUsers: isCompanyAdmin ? 8 : 1
        });
        
        setRecentActivity([
          {
            id: 1,
            type: 'upload',
            message: 'Electronics catalog uploaded successfully',
            timestamp: new Date(Date.now() - 300000),
            status: 'completed'
          },
          {
            id: 2,
            type: 'processing',
            message: 'Product matching in progress for supplier_data.xlsx',
            timestamp: new Date(Date.now() - 600000),
            status: 'processing'
          },
          {
            id: 3,
            type: 'match',
            message: '1,247 new product matches found',
            timestamp: new Date(Date.now() - 900000),
            status: 'completed'
          }
        ]);
        
      } catch (error) {
        console.error('Error loading dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, [isCompanyAdmin]);

  const handleStartAnalysis = () => {
    navigate('/file-upload-dashboard');
  };

  const handleUserManagement = () => {
    navigate('/user-management');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="pt-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Welcome Section */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Welcome back, {userProfile?.full_name || 'User'}!
            </h1>
            <p className="text-gray-600">
              {company?.name} - {userProfile?.role === 'company_admin' ? 'Administrator' : 'User'}
            </p>
          </div>

          {/* Quick Actions */}
          <div className="mb-8 flex flex-wrap gap-4">
            <button
              onClick={handleStartAnalysis}
              className="flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Upload className="h-5 w-5 mr-2" />
              Start New Analysis
            </button>
            
            {isCompanyAdmin && (
              <button
                onClick={handleUserManagement}
                className="flex items-center px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <Users className="h-5 w-5 mr-2" />
                Manage Users
              </button>
            )}
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <FileText className="h-8 w-8 text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Total Files</p>
                  <p className="text-2xl font-semibold text-gray-900">{stats?.totalFiles}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Clock className="h-8 w-8 text-yellow-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Processing</p>
                  <p className="text-2xl font-semibold text-gray-900">{stats?.processingFiles}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <TrendingUp className="h-8 w-8 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Matches Found</p>
                  <p className="text-2xl font-semibold text-gray-900">{stats?.completedMatches?.toLocaleString()}</p>
                </div>
              </div>
            </div>

            {isCompanyAdmin && (
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <Users className="h-8 w-8 text-purple-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">Active Users</p>
                    <p className="text-2xl font-semibold text-gray-900">{stats?.activeUsers}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Recent Activity */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900 flex items-center">
                <Activity className="h-5 w-5 mr-2" />
                Recent Activity
              </h2>
            </div>
            <div className="divide-y divide-gray-200">
              {recentActivity?.map((activity) => (
                <div key={activity?.id} className="px-6 py-4 flex items-center">
                  <div className="flex-shrink-0">
                    {activity?.status === 'completed' ? (
                      <CheckCircle className="h-5 w-5 text-green-500" />
                    ) : (
                      <Clock className="h-5 w-5 text-yellow-500" />
                    )}
                  </div>
                  <div className="ml-4 flex-1">
                    <p className="text-sm text-gray-900">{activity?.message}</p>
                    <p className="text-xs text-gray-500">
                      {activity?.timestamp?.toLocaleDateString()} at {activity?.timestamp?.toLocaleTimeString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            
            {recentActivity?.length === 0 && (
              <div className="px-6 py-8 text-center text-gray-500">
                <Activity className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>No recent activity</p>
                <p className="text-sm">Start by uploading your first file</p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;